import React, { useEffect, useState, useRef, lazy, Suspense } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";

const HeroCanvas = lazy(() => import("./HeroCanvas"));

const threats = [
  { text: "Scraper detected", origin: "RU", type: "blocked" },
  { text: "Click fraud attempt", origin: "CN", type: "blocked" },
  { text: "Credential stuffing", origin: "BR", type: "blocked" },
  { text: "SEO crawler", origin: "US", type: "flagged" },
  { text: "DDoS bot cluster", origin: "UA", type: "blocked" },
  { text: "Form spam bot", origin: "IN", type: "blocked" },
  { text: "Price scraper", origin: "DE", type: "blocked" },
  { text: "API abuse detected", origin: "NG", type: "blocked" },
];

const ThreatIndicator = React.forwardRef<HTMLDivElement, { threat: (typeof threats)[0]; style: React.CSSProperties }>(
  ({ threat, style }, ref) => (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.9, y: 5 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: -5 }}
      transition={{ duration: 0.5 }}
      className="absolute pointer-events-none"
      style={style}
    >
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-card/80 backdrop-blur-sm border border-border/50">
        <span className={`w-1.5 h-1.5 rounded-full ${threat.type === "blocked" ? "bg-destructive" : "bg-amber"} animate-pulse`} />
        <span className="text-[9px] font-mono text-muted-foreground whitespace-nowrap">
          {threat.text} — <span className="text-foreground/70">{threat.origin}</span>
        </span>
      </div>
    </motion.div>
  )
);

const Hero = () => {
  const TARGET = 47291;
  const [count, setCount] = useState(0);
  const [bootDone, setBootDone] = useState(false);
  const [canvasReady, setCanvasReady] = useState(false);
  const [activeThreats, setActiveThreats] = useState<Array<{ id: number; threat: typeof threats[0]; style: React.CSSProperties }>>([]);
  const nextId = useRef(0);
  const isMobile = useIsMobile();

  // Defer canvas loading to after first paint
  useEffect(() => {
    if (isMobile) return; // Skip canvas entirely on mobile
    const id = typeof requestIdleCallback !== "undefined"
      ? requestIdleCallback(() => setCanvasReady(true), { timeout: 1500 })
      : setTimeout(() => setCanvasReady(true), 200);
    return () => {
      if (typeof cancelIdleCallback !== "undefined" && typeof id === "number") {
        cancelIdleCallback(id);
      } else {
        clearTimeout(id as number);
      }
    };
  }, [isMobile]);

  // Boot sequence: count from 0 to TARGET over 2s with easing
  useEffect(() => {
    const duration = 2000;
    const startTime = performance.now();
    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * TARGET));
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        setBootDone(true);
      }
    };
    requestAnimationFrame(step);
  }, []);

  // Normal tick after boot
  useEffect(() => {
    if (!bootDone) return;
    const interval = setInterval(() => {
      setCount((c) => c + Math.floor(Math.random() * 17 + 3));
    }, 800);
    return () => clearInterval(interval);
  }, [bootDone]);

  useEffect(() => {
    const positions = [
      { top: "15%", left: "5%" },
      { top: "25%", right: "8%" },
      { top: "60%", left: "3%" },
      { top: "70%", right: "5%" },
      { top: "40%", left: "8%" },
      { top: "80%", right: "12%" },
    ];

    const spawn = () => {
      const threat = threats[Math.floor(Math.random() * threats.length)];
      const pos = positions[Math.floor(Math.random() * positions.length)];
      const id = nextId.current++;

      setActiveThreats((prev) => [...prev.slice(-3), { id, threat, style: pos }]);

      setTimeout(() => {
        setActiveThreats((prev) => prev.filter((t) => t.id !== id));
      }, 3500);
    };

    spawn();
    const interval = setInterval(spawn, 2200);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {canvasReady && (
        <Suspense fallback={null}>
          <HeroCanvas />
        </Suspense>
      )}

      <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background" />
      <div className="absolute inset-0 bg-gradient-to-r from-background/60 via-transparent to-background/60" />
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-background to-transparent" />

      <div className="absolute inset-0 hidden lg:block">
        <AnimatePresence>
          {activeThreats.map((t) => (
            <ThreatIndicator key={t.id} threat={t.threat} style={t.style} />
          ))}
        </AnimatePresence>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 text-center pt-20 md:pt-[88px]">
        <h1
          className="font-display font-extrabold text-[clamp(3rem,10vw,9rem)] leading-[0.95] tracking-[-0.03em] mb-6"
        >
          <span className="block text-foreground">Your traffic</span>
          <span className="block text-foreground">is lying</span>
          <span className="block text-shimmer-cyan">to you.</span>
        </h1>

        <p
          className="text-xs sm:text-base md:text-lg text-muted-foreground max-w-md mx-auto mb-6 font-mono whitespace-nowrap animate-[fadeSlideUp_0.6s_ease-out_0.3s_both]"
        >
          Real-time bot detection. One line of code.
        </p>

        <div
          className="flex items-center justify-center gap-3 mb-8 animate-[fadeIn_0.4s_ease-out_0.5s_both]"
        >
          <span className="inline-block w-2 h-2 rounded-full bg-green animate-pulse" />
          <span className="font-data text-base md:text-lg text-green tracking-wider">
            {count.toLocaleString()}
          </span>
          <span className="text-[11px] text-muted-foreground font-mono">bots blocked this week</span>
        </div>

        <div
          className="flex flex-col items-center animate-[fadeSlideUp_0.5s_ease-out_0.6s_both]"
        >
          <Link to="/auth">
            <Button
              size="lg"
              className="btn-shimmer bg-primary text-primary-foreground hover:bg-primary/90 glow-cyan-strong px-10 h-14 text-sm font-display font-semibold tracking-wide group"
            >
              See Your Real Traffic
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
          <p className="text-[10px] text-muted-foreground font-mono mt-4 tracking-wider">
            No credit card · Free plan · 30-second setup
          </p>

        </div>
      </div>
    </section>
  );
};

export default Hero;
